
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import { useAuth } from '../../contexts/AuthContext';
import { TERMS_VERSION } from '../../contexts/authConstants';
import TermsConsentModal from '../../components/feature/TermsConsentModal';
import SupabaseUserService from '../../services/supabaseUserService';
import { logger } from '../../utils/logger';
import { useWebViewDetection } from '../../hooks/useWebViewDetection';
import { getFeatureFlags } from '../../utils/environmentConfig';

export default function SignUpPage() {
  const navigate = useNavigate();
  const { signUp, signInWithGoogle } = useAuth();
  const { isStandalone } = useWebViewDetection();
  const featureFlags = getFeatureFlags();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [isSubmittingConsent, setIsSubmittingConsent] = useState(false);
  const [userIdAfterSignup, setUserIdAfterSignup] = useState<string | null>(null);
  const userService = SupabaseUserService.getInstance();
  const shouldHideLogin = featureFlags.requirePWAForLogin && !isStandalone;

  const getConsentStorageKey = (uid: string) => `magistral.terms.acceptedVersion.${uid}`;

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  // Verifica consentimento da versão atual e abre o modal se necessário
  const maybeOpenConsentForUser = async (uid: string): Promise<boolean> => {
    try {
      // Short-circuit via localStorage (por usuário)
      try {
        const localAccepted = localStorage.getItem(getConsentStorageKey(uid));
        if (localAccepted === TERMS_VERSION) {
          // Limpa chave legada (sem userId) se ainda existir
          localStorage.removeItem('magistral.terms.acceptedVersion');
          return false; // já aceitou esta versão
        }
        // Caso ainda exista a chave antiga igual, remove para evitar falso positivo em novos usuários
        const legacyValue = localStorage.getItem('magistral.terms.acceptedVersion');
        if (legacyValue) {
          localStorage.removeItem('magistral.terms.acceptedVersion');
        }
      } catch { /* ignore */ }

      const { data: consent, error: consentError } = await supabase
        .from('user_consents')
        .select('id')
        .eq('user_id', uid)
        .eq('version', TERMS_VERSION)
        .order('accepted_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (consentError && consentError.code !== 'PGRST116') {
        logger.warn('[SignUpPage] Erro ao verificar consentimento:', consentError);
      }

      if (!consent) {
        setUserIdAfterSignup(uid);
        setShowConsentModal(true);
        return true; // modal aberto
      }
      return false; // já tem consentimento
    } catch (e) {
      logger.warn('[SignUpPage] Exceção na verificação de consentimento, abrindo modal por segurança:', e);
      setUserIdAfterSignup(uid);
      setShowConsentModal(true);
      return true;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    logger.debug('Iniciando processo de cadastro...');
    setError('');
    
    if (!formData.acceptTerms) {
      logger.debug('Termos não aceitos');
      setError('Você deve aceitar os Termos de Uso e Política de Privacidade para criar uma conta');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      logger.debug('Senhas não coincidem');
      setError('As senhas não coincidem');
      return;
    }

    if (formData.password.length < 6) {
      logger.debug('Senha muito curta');
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    logger.debug('Validações passaram, iniciando cadastro...');
    logger.sensitive('Dados:', { name: formData.name, email: formData.email });
    setLoading(true);

    try {
      logger.debug('Chamando signUp...');
      const user = await signUp(formData.email, formData.password, formData.name);

      if (user) {
        logger.success('Cadastro enviado; redirecionando para tela de confirmação');
        setEmailSent(true);
        navigate('/signup/email-sent', { state: { email: formData.email } });
      } else {
        setError('Não foi possível criar a conta. Tente novamente.');
        setLoading(false);
      }
      
    } catch (error: any) {
      logger.error('Erro ao criar usuário:', error);
      setError(error.message || 'Erro ao criar conta. Tente novamente.');
      setLoading(false);
    }
  };

  const goToLogin = () => {
    navigate('/login');
  };

  const goBack = () => {
    navigate('/');
  };

  const handleGoogleSignUp = async () => {
    setError('');
    setGoogleLoading(true);

    try {
      await signInWithGoogle('/setup-family');
    } catch (err: any) {
      console.error('❌ [SignUpPage] Erro no cadastro com Google:', err);
      setError(err?.message || 'Não foi possível autenticar com Google.');
      setGoogleLoading(false);
    }
  };

  const handleConsentAccept = async () => {
    if (!userIdAfterSignup) return;
    setIsSubmittingConsent(true);
    try {
      let ip: string | undefined;
      try {
        const resp = await fetch('https://api.ipify.org?format=json');
        const json = await resp.json();
        ip = json?.ip;
      } catch {
        // silencioso se falhar
      }

      await userService.registerConsent({
        userId: userIdAfterSignup,
        termsAccepted: true,
        privacyPolicyAccepted: true,
        version: TERMS_VERSION,
        ipAddress: ip,
        userAgent: navigator.userAgent
      });

      // Cache local da versão aceita (evita reexibição no PWA instalado)
      try {
        const storageKey = getConsentStorageKey(userIdAfterSignup);
        localStorage.setItem(storageKey, TERMS_VERSION);
        localStorage.removeItem('magistral.terms.acceptedVersion');
      } catch {
        /* ignore localStorage errors (private mode, quota, etc.) */
      }

      console.log('🔄 Redirecionando para /setup-family após consentimento');
      navigate('/setup-family');
    } catch (e) {
      console.error('❌ Falha ao registrar consentimento:', e);
      setError('Não foi possível registrar o consentimento. Tente novamente.');
      setIsSubmittingConsent(false);
    }
  };

  const handleConsentDecline = async () => {
    try {
      await supabase.auth.signOut();
      setShowConsentModal(false);
      navigate('/signup');
    } catch (e) {
      console.error('❌ Erro ao fazer logout:', e);
      navigate('/signup');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-t from-green-200 to-white flex flex-col items-center justify-center px-6 py-8">
      {/* Header com botão voltar */}
      <div className="w-full flex justify-start mb-6">
        <button
          onClick={goBack}
          className="w-10 h-10 bg-white rounded-2xl flex items-center justify-center shadow-md hover:bg-gray-50 transition-colors"
        >
          <i className="ri-arrow-left-line text-gray-600 text-xl"></i>
        </button>
      </div>

      {/* Logo e Header */}
      <div className="text-center mb-8">
        <div className="flex justify-center items-center mb-4">
          <div className="w-24 h-24 bg-transparent rounded-3xl flex items-center justify-center shadow-lg overflow-hidden">
            <img 
              src="/Imagens/logo 2.0.png" 
              alt="Logo Magistral 2.0" 
              className="w-full h-full object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const fallbackDiv = document.createElement('div');
                fallbackDiv.className = 'w-20 h-20 bg-emerald-500 rounded-3xl flex items-center justify-center';
                const icon = document.createElement('i');
                icon.className = 'ri-heart-pulse-fill text-3xl text-white';
                fallbackDiv.appendChild(icon);
                target.parentNode?.appendChild(fallbackDiv);
              }}
            />
          </div>
        </div>
        <h1 className="text-3xl font-bold text-gray-800 font-sans" style={{ 
          fontWeight: 700,
          textShadow: '1px 2px 4px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.05)'
        }}>
          Magistral
        </h1>
        <p className="text-gray-600 mt-2 font-sans" style={{ fontWeight: 500 }}>
          Vamos começar juntos!
        </p>
      </div>

      {/* Formulário */}
      <div className="w-full max-w-sm bg-white rounded-3xl p-6 shadow-xl">
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">
            Criar sua conta
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Preencha os dados para começar
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <Input
            label="Nome completo"
            type="text"
            placeholder="Seu nome completo"
            value={formData.name}
            onChange={handleInputChange('name')}
            disabled={emailSent || loading}
            required
          />

          <Input
            label="E-mail"
            type="email"
            placeholder="seu@email.com"
            value={formData.email}
            onChange={handleInputChange('email')}
            disabled={emailSent || loading}
            required
          />
          
          <Input
            label="Senha"
            type="password"
            placeholder="Crie uma senha"
            value={formData.password}
            onChange={handleInputChange('password')}
            disabled={emailSent || loading}
            required
          />

          <Input
            label="Confirmar Senha"
            type="password"
            placeholder="Confirme sua senha"
            value={formData.confirmPassword}
            onChange={handleInputChange('confirmPassword')}
            disabled={emailSent || loading}
            required
          />

          <div className="flex items-start space-x-3 mt-4">
          <input
              type="checkbox"
              id="acceptTerms"
              checked={formData.acceptTerms}
              onChange={(e) => setFormData(prev => ({ ...prev, acceptTerms: e.target.checked }))}
              className="mt-1 w-4 h-4 text-emerald-600 bg-gray-100 border-gray-300 rounded focus:ring-emerald-500 focus:ring-2"
              disabled={emailSent || loading}
              required
            />
            <label htmlFor="acceptTerms" className="text-xs text-gray-600 leading-relaxed">
              Eu li e aceito os{' '}
              <Link to="/terms-and-privacy" className="text-emerald-500 hover:text-emerald-600 underline">
                Termos de Uso
              </Link> e{' '}
              <Link to="/terms-and-privacy" className="text-emerald-500 hover:text-emerald-600 underline">
                Política de Privacidade
              </Link>
            </label>
          </div>

          <Button type="submit" size="lg" className="w-full mt-6" disabled={emailSent || loading}>
            {loading ? (
              <>
                <i className="ri-loader-4-line mr-2 animate-spin"></i>
                Criando conta...
              </>
            ) : (
              <>
                <i className="ri-user-add-line mr-2"></i>
                Criar Conta
              </>
            )}
          </Button>
        </form>

        {!shouldHideLogin && (
          <>
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-white text-gray-500">ou continue com</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full flex items-center justify-center gap-3"
              onClick={handleGoogleSignUp}
              disabled={googleLoading}
            >
              {googleLoading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Conectando...
                </>
              ) : (
                <>
                  <img
                    src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                    alt="Google"
                    className="w-5 h-5"
                  />
                  Criar conta com Google
                </>
              )}
            </Button>

            <div className="text-center mt-6">
              <p className="text-gray-600 text-sm">
                Já tem uma conta?{' '}
                <button
                  onClick={goToLogin}
                  className="text-emerald-500 font-medium hover:text-emerald-600 transition-colors"
                >
                  Fazer Login
                </button>
              </p>
            </div>
          </>
        )}
      </div>

      {/* Benefícios */}
      <div className="grid grid-cols-3 gap-4 mt-8 w-full max-w-sm">
        <div className="text-center">
          <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mb-2 mx-auto shadow-md">
            <i className="ri-shield-check-line text-emerald-500 text-[1.67rem]"></i>
          </div>
          <p className="text-xs text-gray-600">Seguro</p>
        </div>
        <div className="text-center">
          <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mb-2 mx-auto shadow-md">
            <img src="/Imagens/icons8-família-de-dois-homens-64.png" alt="Família" className="w-8 h-8" />
          </div>
          <p className="text-xs text-gray-600">Família</p>
        </div>
        <div className="text-center">
          <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mb-2 mx-auto shadow-md">
            <i className="ri-heart-line text-emerald-500 text-[1.67rem]"></i>
          </div>
          <p className="text-xs text-gray-600">Saúde</p>
        </div>
      </div>

      <TermsConsentModal
        isOpen={showConsentModal}
        isSubmitting={isSubmittingConsent}
        termsVersion={TERMS_VERSION}
        onDecline={handleConsentDecline}
        onAccept={handleConsentAccept}
      />
    </div>
  );
}
